import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger, 
  DropdownMenuSeparator 
} from "@/components/ui/dropdown-menu";
import { 
  Plus, 
  MoreVertical, 
  Edit, 
  Trash2, 
  Mail, 
  Phone, 
  CheckCircle, 
  AlertCircle 
} from "lucide-react";

export function SchoolAdminsTab({
  admins,
  currentAdmin,
  loading,
  canManageAdmins,
  onAddAdmin,
  onEditAdmin,
  onUpdateAdmin
}) {
  if (!canManageAdmins) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="py-12 text-center">
          <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-700 mb-2">Access Restricted</h3>
          <p className="text-slate-500">You don't have permission to manage administrators.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">School Administrators</h2>
          <p className="text-sm text-slate-500">Manage users who can access this portal</p>
        </div>
        <Button 
          onClick={onAddAdmin}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="add-admin-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Admin
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="grid gap-4">
          {admins.map((adminUser) => (
            <Card key={adminUser.id} className="border-0 shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                      <span className="text-lg font-semibold text-emerald-600">
                        {adminUser.name?.charAt(0) || "A"}
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-slate-800">{adminUser.name}</h3>
                        {adminUser.id === currentAdmin.id && (
                          <Badge variant="outline" className="text-xs">You</Badge>
                        )}
                        {adminUser.role === "school_super_admin" && (
                          <Badge className="bg-purple-100 text-purple-700 text-xs">Primary</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500">{adminUser.designation || adminUser.role?.replace("_", " ")}</p>
                      <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {adminUser.email}
                        </span>
                        {adminUser.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {adminUser.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Badge 
                      className={adminUser.status === "active" 
                        ? "bg-green-100 text-green-700" 
                        : "bg-slate-100 text-slate-600"
                      }
                    >
                      {adminUser.status === "active" ? (
                        <><CheckCircle className="w-3 h-3 mr-1" /> Active</>
                      ) : (
                        adminUser.status
                      )}
                    </Badge>
                    
                    {adminUser.role !== "school_super_admin" && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onEditAdmin(adminUser)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit Permissions
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => onUpdateAdmin(adminUser.id, { status: "inactive" })}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Deactivate
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>

                {/* Permissions */}
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-xs text-slate-500 mb-2">Permissions</p>
                  <div className="flex flex-wrap gap-1">
                    {(adminUser.permissions || []).map(perm => (
                      <Badge key={perm} variant="secondary" className="text-xs">
                        {perm.replace(/_/g, " ")}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
